import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { CheckCircle, ArrowRight, Truck, Star } from "lucide-react";

const SITE = SITE_URL;

const bjModels = [
  {
    slug: "baby-jogger-city-mini-gt2-single",
    name: "Baby Jogger City Mini GT2 Single",
    type: "Single",
    price: 22,
    highlight: "Best-selling single",
    desc: "All-terrain single with hand-operated front brake, one-step fold and near-flat recline for park naps. Handles Disney's varied surfaces with ease.",
  },
  {
    slug: "baby-jogger-city-mini-gt2-double",
    name: "Baby Jogger City Mini GT2 Double",
    type: "Double",
    price: 34,
    highlight: "Top double stroller",
    desc: "The GT2 Double carries two children side-by-side with independent recline on each seat. All-terrain wheels roll over curbs and park paths without slowing down.",
  },
  {
    slug: "baby-jogger-gt-double",
    name: "Baby Jogger GT Double",
    type: "Double",
    price: 32,
    highlight: "All-terrain double",
    desc: "A rugged side-by-side double stroller built for outdoor and theme-park terrain. Large sun canopy, UPF 50+ coverage and easy one-hand fold.",
  },
  {
    slug: "baby-jogger-city-mini-double",
    name: "Baby Jogger City Mini Double",
    type: "Double",
    price: 30,
    highlight: "Great value double",
    desc: "Classic double stroller with the iconic City Mini design. Lightweight, maneuverable and folds flat with both seats attached — quick to stow on Disney buses.",
  },
];

const faqs = [
  {
    q: "Are Baby Jogger strollers allowed in Disney parks?",
    a: "Yes. All of our Baby Jogger stroller models fit within Disney World's size requirement of 31 inches wide and 52 inches long. They are allowed in all four Disney parks and on Disney transportation.",
  },
  {
    q: "What makes the Baby Jogger GT2 good for Disney World?",
    a: "The Baby Jogger City Mini GT2 has all-terrain wheels that handle Disney's varied surfaces — from cobblestones in Magic Kingdom to gravel paths at Animal Kingdom. The hand-operated front brake, one-step fold, and near-flat recline make it one of our most family-friendly park strollers. It is our best-selling single stroller.",
  },
  {
    q: "Can I rent a Baby Jogger double stroller for two children at Disney?",
    a: "Yes. We carry three Baby Jogger double models: the GT2 Double, GT Double and City Mini Double. All are side-by-side designs with independent recline. Each fits Disney's stroller size policy.",
  },
  {
    q: "How does the Baby Jogger GT2 Double compare to the Zoe Twin for Disney?",
    a: "The Baby Jogger GT2 Double has all-terrain wheels and a slightly larger frame, making it better for outdoor terrain and longer days. The Zoe Twin is lighter and more compact. For Disney families who prioritize weight, Zoe is the pick. For a smoother all-terrain ride, Baby Jogger is the better choice.",
  },
  {
    q: "Do you deliver Baby Jogger strollers to Disney resorts?",
    a: "Yes. We deliver all Baby Jogger models to every Walt Disney World resort. Delivery and pickup require an in-person lobby meeting — Disney does not allow third-party vendors to leave equipment with bell services.",
  },
  {
    q: "Is delivery free for Baby Jogger stroller rentals at Disney World?",
    a: "Yes. Standard delivery is free to all Walt Disney World resorts, Universal Orlando, Kissimmee vacation homes and area hotels. Delivery is always free — no early-delivery surcharges.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Baby Jogger Stroller Rental Disney World", item: `${SITE}/baby-jogger-stroller-rental-disney-world` },
  ],
};

export default function BabyJoggerStrollerRental() {
  return (
    <>
      <PageSEO
        title="Baby Jogger Stroller Rental Near Disney World | City Mini GT2 Orlando"
        description="Rent a Baby Jogger City Mini GT2 Single, GT2 Double or City Mini Double near Walt Disney World. All-terrain, park-ready strollers with free resort delivery. From $22/day. Enchanted Gear Rentals."
        canonical={`${SITE}/baby-jogger-stroller-rental-disney-world`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Baby Jogger Stroller Rental</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-[#FFD166]/10 border border-[#FFD166]/30 text-[#FFD166] text-sm font-medium px-4 py-1.5 rounded-full mb-5">
              <Star className="w-4 h-4" /> Best-selling stroller brand at Disney
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Baby Jogger Stroller Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              The Baby Jogger City Mini GT2 is our best-selling stroller — and the most popular choice for Disney World families who want all-terrain performance in a compact, quick-fold design. We carry the GT2 Single and three double models, all delivered free to your Disney resort.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5] italic">Not affiliated with The Walt Disney Company.</p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="bj-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/strollers"><button className="btn-ghost">Browse All Strollers</button></Link>
            </div>
          </div>
        </section>

        {/* Models */}
        <section className="container py-14">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Baby Jogger models available to rent</h2>
          <p className="text-[#B9C8D5] mb-10 max-w-2xl">Every stroller is cleaned, sanitized and inspected before delivery. All models fit Disney's park stroller size requirement.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {bjModels.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-6 transition-all hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full">{s.type}</div>
                  <span className="text-xs text-[#FFD166] bg-[#FFD166]/10 px-2 py-0.5 rounded-full">{s.highlight}</span>
                </div>
                <h3 className="font-serif text-xl text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-2">{s.name}</h3>
                <p className="text-sm text-[#B9C8D5] mb-4">{s.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[#FFD166] font-semibold">From ${s.price}/day</span>
                  <span className="text-xs text-[#B9C8D5] flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Why BJ at Disney */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">Why Baby Jogger is a Disney World favorite</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {[
              { icon: CheckCircle, title: "All-terrain wheels for Disney terrain", body: "Disney parks include everything from Magic Kingdom's cobblestones to Animal Kingdom's rugged paths. Baby Jogger's all-terrain wheels handle every surface without slowing you down." },
              { icon: CheckCircle, title: "Near-flat recline for naps", body: "The GT2 reclines to nearly flat — so toddlers can nap in the stroller while you keep exploring the parks. No need to rush back to the resort at naptime." },
              { icon: Truck, title: "Free delivery to every Disney resort", body: "We deliver to all Walt Disney World resorts and Universal Orlando hotels. All Disney resort deliveries require an in-person lobby meeting — no bell-services drop-offs." },
              { icon: Star, title: "One-step fold under 10 seconds", body: "Pull one strap and the Baby Jogger City Mini GT2 collapses in under ten seconds. Stroller-folding at Disney bus stops becomes effortless." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <Icon className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#F6F1E4]">{title}</div>
                  <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Disney size note */}
        <section className="container py-10">
          <div className="max-w-2xl rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Disney stroller size rules — do Baby Joggers qualify?</h2>
            <p className="text-[#B9C8D5] text-sm mb-4">
              Walt Disney World requires strollers to be no larger than 31 inches wide and 52 inches long. Every Baby Jogger model we carry fits within those dimensions.
            </p>
            <ul className="space-y-2 text-sm text-[#B9C8D5]">
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />All Baby Jogger models meet Disney's 31″ × 52″ stroller size limit</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Fold when boarding Disney buses, Monorail and Skyliner</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Stroller parking available at every theme park entrance</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Use your own ID tag or the label we provide at delivery</li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Baby Jogger stroller rental FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Compare other stroller brands</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Zoe stroller rental Disney", to: "/zoe-stroller-rental-disney-world" },
              { label: "Nuna stroller rental Disney", to: "/nuna-stroller-rental-disney-world" },
              { label: "Disney World stroller rentals", to: "/disney-world-stroller-rentals" },
              { label: "Double stroller rentals Orlando", to: "/double-stroller-rentals-orlando" },
              { label: "Stroller quiz", to: "/stroller-quiz" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your Baby Jogger for Disney World</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and resort to check availability and confirm your Baby Jogger stroller rental.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="bj-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
